/**
 * MechaGuard - MusicSequencer (程序化背景音乐)
 *
 * 不依赖任何音频文件：用振荡器与噪声实时合成一段循环的科幻配乐。
 *
 * 结构
 *   和声：4 小节循环，Am - F - C - G（A 小调 i - VI - III - VII，
 *         科幻 / 史诗题材最常见的进行之一）
 *   网格：每小节 16 个十六分音符
 *   分层：随强度（intensity）增减
 *         menu   仅铺底 pad + 稀疏琶音
 *         combat 加贝斯与鼓组
 *         boss   琶音加倍、速度提到 148 BPM
 *
 * 调度
 *   用 Web Audio 标准的「提前调度」模式：setInterval 每 40ms 醒一次，
 *   把未来 120ms 内该发的音排进时间轴。
 *   如果直接按 currentTime 立刻发声，主线程一卡顿节奏就会抖。
 */

const LOOKAHEAD = 0.12;   // 提前调度的时长（秒）
const TICK_MS = 40;       // 调度器唤醒间隔
const STEPS_PER_BAR = 16;
const BARS = 4;

const SEMITONES = {
  C: -9, 'C#': -8, D: -7, 'D#': -6, E: -5, F: -4,
  'F#': -3, G: -2, 'G#': -1, A: 0, 'A#': 1, B: 2,
};

/**
 * 音名 -> 频率（十二平均律，A4 = 440）
 * @param {string} name 例如 'A4' / 'F#2'
 */
export function noteFreq(name) {
  const m = /^([A-G]#?)(-?\d)$/.exec(name);
  if (!m) return 440;
  const semi = SEMITONES[m[1]];
  const octave = Number(m[2]);
  return 440 * Math.pow(2, (semi + (octave - 4) * 12) / 12);
}

/** 和弦进行（每小节一项） */
const PROGRESSION = [
  { bass: 'A2', chord: ['A3', 'C4', 'E4'] },
  { bass: 'F2', chord: ['F3', 'A3', 'C4'] },
  { bass: 'C3', chord: ['C4', 'E4', 'G4'] },
  { bass: 'G2', chord: ['G3', 'B3', 'D4'] },
];

/** 各强度对应的速度（BPM） */
const TEMPO = { menu: 96, combat: 124, boss: 148 };

export class MusicSequencer {
  /**
   * @param {object} opts
   * @param {AudioContext} opts.ctx
   * @param {AudioNode} opts.destination 已接好音量控制的输入节点
   */
  constructor({ ctx, destination }) {
    this.ctx = ctx;
    this.destination = destination;

    /** @type {'menu'|'combat'|'boss'|'off'} */
    this.intensity = 'menu';
    this.playing = false;

    this._timer = null;
    this._step = 0;
    this._nextTime = 0;
    this._noiseBuf = null;
  }

  /* ===================================================
   * 控制
   * =================================================== */

  /** @param {'menu'|'combat'|'boss'|'off'} mode */
  setIntensity(mode) {
    if (mode === this.intensity && this.playing === (mode !== 'off')) return;
    this.intensity = mode;

    if (mode === 'off') this.stop();
    else this.start();
  }

  start() {
    if (this.playing || !this.ctx) return;

    this.playing = true;
    this._step = 0;
    this._nextTime = this.ctx.currentTime + 0.12;
    this._timer = setInterval(() => this._tick(), TICK_MS);
  }

  stop() {
    this.playing = false;
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  dispose() {
    this.stop();
    this.ctx = null;
    this.destination = null;
  }

  /* ===================================================
   * 调度
   * =================================================== */

  _tempo() {
    return TEMPO[this.intensity] || TEMPO.menu;
  }

  /** 一个十六分音符的时长 */
  _stepDur() {
    return 60 / this._tempo() / 4;
  }

  /** 一小节的时长 */
  _barDur() {
    return this._stepDur() * STEPS_PER_BAR;
  }

  _tick() {
    if (!this.playing || !this.ctx) return;

    const horizon = this.ctx.currentTime + LOOKAHEAD;

    // guard 防止 _stepDur() 异常为 0 时死循环
    let guard = 0;
    while (this._nextTime < horizon && guard < 64) {
      guard += 1;
      this._scheduleStep(this._step, this._nextTime);
      this._step = (this._step + 1) % (STEPS_PER_BAR * BARS);
      this._nextTime += this._stepDur();
    }
  }

  /**
   * 把第 step 个十六分音符要发的音排进时间轴
   * @param {number} step
   * @param {number} time 绝对时间（audioContext 时间轴）
   */
  _scheduleStep(step, time) {
    const bar = Math.floor(step / STEPS_PER_BAR) % BARS;
    const beat = step % STEPS_PER_BAR;
    const prog = PROGRESSION[bar];

    const isMenu = this.intensity === 'menu';

    // ---- 铺底和弦：每小节一次 ----
    if (beat === 0) {
      const dur = this._barDur() * 0.95;
      for (const note of prog.chord) this._pad(noteFreq(note), time, dur);
    }

    // ---- 贝斯：每拍一次 ----
    if (!isMenu && beat % 4 === 0) {
      this._bass(noteFreq(prog.bass), time, this._stepDur() * 3.2);
    }

    // ---- 琶音：combat 每 4 步、boss 每 2 步 ----
    const arpDiv = this.intensity === 'boss' ? 2 : 4;
    if (beat % arpDiv === 0) {
      const idx = Math.floor(step / arpDiv) % prog.chord.length;
      // 高八度，让琶音浮在铺底之上
      this._arp(noteFreq(prog.chord[idx]) * 2, time, this._stepDur() * arpDiv * 0.85);
    }

    // ---- 鼓组 ----
    if (!isMenu) {
      if (beat % 8 === 0) this._kick(time);
      if (beat % 8 === 4) this._snare(time);
      if (beat % 2 === 0) this._hat(time, beat % 4 === 0 ? 0.055 : 0.028);
    }
  }

  /* ===================================================
   * 音色
   * =================================================== */

  /** 长音铺底：两个轻微失谐的锯齿过低通 */
  _pad(freq, time, dur) {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.055, time + 0.45);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1300;
    filter.Q.value = 0.7;

    g.connect(filter);
    filter.connect(this.destination);

    for (const detune of [-7, 7]) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      osc.connect(g);
      osc.start(time);
      osc.stop(time + dur + 0.1);
    }
  }

  /** 贝斯：锯齿 + 随时间下沉的低通，做出「哇」的音头 */
  _bass(freq, time, dur) {
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(720, time);
    filter.frequency.exponentialRampToValueAtTime(180, time + dur);
    filter.Q.value = 6;

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.15, time + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);

    osc.connect(filter);
    filter.connect(g);
    g.connect(this.destination);

    osc.start(time);
    osc.stop(time + dur + 0.05);
  }

  /** 琶音：方波短音 */
  _arp(freq, time, dur) {
    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.04, time + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);

    osc.connect(g);
    g.connect(this.destination);

    osc.start(time);
    osc.stop(time + dur + 0.05);
  }

  /** 底鼓：正弦从 140Hz 迅速滑到 45Hz */
  _kick(time) {
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, time);
    osc.frequency.exponentialRampToValueAtTime(45, time + 0.12);

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.45, time + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.2);

    osc.connect(g);
    g.connect(this.destination);

    osc.start(time);
    osc.stop(time + 0.25);
  }

  /** 军鼓：带通噪声 */
  _snare(time) {
    this._noise(time, 0.14, 0.16, 'bandpass', 1800, 0.9);
  }

  /** 踩镲：高通噪声，极短 */
  _hat(time, gain) {
    this._noise(time, 0.045, gain, 'highpass', 7000, 0.7);
  }

  /** 噪声源 + 滤波 + 包络 */
  _noise(time, dur, gain, type, freq, q) {
    const src = this.ctx.createBufferSource();
    src.buffer = this._noiseBuffer();
    src.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;

    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), time + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);

    src.connect(filter);
    filter.connect(g);
    g.connect(this.destination);

    src.start(time);
    src.stop(time + dur + 0.02);
  }

  /** 1 秒白噪声缓冲，全程复用（每次新建会浪费内存） */
  _noiseBuffer() {
    if (this._noiseBuf) return this._noiseBuf;

    const length = Math.max(1, Math.floor(this.ctx.sampleRate * 1));
    const buf = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buf.getChannelData(0);

    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;

    this._noiseBuf = buf;
    return buf;
  }
}
